import React, { useMemo, useCallback, useRef, useState, useEffect, useContext } from 'react';
import { withTranslation } from 'react-i18next';
import { useDrop, useDrag } from 'react-dnd';
import styled from 'styled-components';
import { COMPLEX_BLOCK_TYPE, COMPLEX_DOCUMENT_PAGE_WRAPPER, CREATIVE_BLOCK, DYNAMIC_TABLE_BLOCK, COMPLEX_EMAIL, REPLACE_FLOW, REUSABLE_SECTION, dummyCallback, BUTTON_BLOCK, LINK_BLOCK } from '../../utils/complexDocumentConstants';
import { EMAIL_DESIGN_TYPE } from '../../utils/complexEmailConstants';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../context/complexDocumentContext';
import { CONTEXT_KEYS, TEMPLATE_TYPE, useArtboardContext } from '../../context/complexArtboardContext';
import { COMPLEX_EMAIL_CONTEXT_KEYS, useEmailContext } from '../../context/complexEmailContext';
import { useSessionContext } from '../../context/sessionContext';
import { detectLanguageDynamic } from '../../utils/services/textGenerationAPI';
import { replaceEmailTemplate, updateMasterTemplateToDocument } from '../../utils/masterTemplateHelper';
import { checkSessionUserAccess } from '../../utils/helper';
import { EditingContainer, PageContent, PagesWrapper } from '../ComplexDocumentDesign/styles';
import { EditorWrapper } from './styles';
import withScrolling from '../../components/ScrollZone';
import BlockLayout from '../ComplexDocumentDesign/BlockLayout';
import VariableMenuContainer from '../ComplexDocumentDesign/VariableMenuContainer';
import createResizeObserver from '../ComplexDocumentDesign/useResizeObserver';
import BlockLayoutSettings from '../ComplexDocumentDesign/BlockLayoutSettings';
import CustomDragLayer from '../SidebarMenu/CustomDragLayer';
import { BusinessLogic } from '../../context/businessLogicContext';
import { isLanguageTranslationDisabled } from '../../utils/detectLanguageHelper';
import ContextMenuContainer from '../ComplexDocumentDesign/contextMenuContainer';

const NAMESPACES = ['COMPLEX_PAGE_OPTIONS','DOCUMENT_DESIGN_SECTION', 'COMPLEX_EMAIL_LAYOUT_SETTINGS' ];

const ToolTipPopup = styled.div``;

const dropAcceptKeys =  [REPLACE_FLOW, ...COMPLEX_BLOCK_TYPE, COMPLEX_EMAIL];

const EmailBackground = ({ pageDetails }) => {
  return (
    <div
      style={{
        position: "absolute",
        backgroundColor: pageDetails?.backgroundColor,
        top: 24,
        left: 0,
        height: 'calc(100% - 48px)',
        width: '100%'
      }}
    />
  )
}

const DesignPage = ({ uuid, pageId, flowNumber, scaling, width, height, backgroundColor, pageOverflowEnabled, isStandalone,
   useStore, getElementStyles ,editable, CurrentDesign, isMobileView, scrollRef  }) => {

  const customSelector = useCallback(() => null, []);
  const [, methods] = useDocumentContext(customSelector, `DesignPage-${uuid}`);

  const customArtboardSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_PAGE] === uuid && store[CONTEXT_KEYS.CURRENT_BLOCK] !== '' , []);
  const [blockEditing, updateArtboard, , , getLatestArtboard] = useArtboardContext(customArtboardSelector, `DesignPage-${uuid}`);

  const currentDesignSelector = useCallback((store) => [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(store[CONTEXT_KEYS.CURRENT_DESIGN]), []);
  const [isDocumentOrFlowDesign] = useArtboardContext(currentDesignSelector, '');

  const textElementSelector = useCallback(store => store[CONTEXT_KEYS.TEXT_ELEMENT_REF], []);
  const [editingBlockOrElement] = useArtboardContext(textElementSelector, `DesignPage-${uuid}`);

  const artboardContextSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK], []);

  const [currentBlock] = useArtboardContext(artboardContextSelector);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const dndFlowSelector = useCallback(store => store[CONTEXT_KEYS.DND_FLOW], []);
  const [isDndFlow] = useArtboardContext(dndFlowSelector, `DesignPage-${uuid}`);

  const { businessLogics, setBusinessLogics } = useContext(BusinessLogic);

  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);

  const detectLanguage = (currentBlock) => {
    if (isLanguageTranslationDisabled(organisationDetails)) return;
    const currentStore = methods.get();
    const blockId = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][currentBlock];
    const templateLanguage = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE];
    const isLanguageDetected = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_LANGUAGE_DETECTED];
    if (!templateLanguage && !isLanguageDetected && blockId) {
      const payload = {
        id: currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
        type: CurrentDesign === "document" ? EMAIL_DESIGN_TYPE.EMAIL : EMAIL_DESIGN_TYPE.EMAIL_BLOCK,
        elementId: blockId,
        elementType: EMAIL_DESIGN_TYPE.EMAIL_BLOCK,
        isStandalone: false,
      };
      detectLanguageDynamic(payload)
        .then(response => {
          const statusCode = response?.statusCode;
          if (statusCode === 734 || statusCode === 742) {
            methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_LANGUAGE_DETECTED]: true });
          }
        })
        .catch(error => console.log({ languageDetectionError: error }));
    }
  };

  const disableEditing = useCallback(() => {
    // Introducing slight deley to delect language so auto save will be finished before detect language api is called
    const timeout = setTimeout(() => {
      detectLanguage(currentBlock);
      clearTimeout(timeout);
    }, 1000);
    updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false, [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false, [CONTEXT_KEYS.CURRENT_BLOCK]: '', [CONTEXT_KEYS.CURRENT_PAGE]: '' });
  }, [detectLanguage, currentBlock]);

  const pageRef = useRef(null);

  const onDrop = useCallback((item, monitor) => {
    if (monitor.didDrop()) return;
    if (item.type === REPLACE_FLOW) {
      if (item.template) {
        const template = item.template;
        updateMasterTemplateToDocument(methods, template.id, item.element).then(({ flows }) => {
          if (flows?.length) {
            methods.replaceFlow({ flow: flows[0], uuid, pageId });
          } else {
            methods.replaceFlow({ flow: item.element, uuid, pageId });
          }
        });
      } else {
        methods.replaceFlow({ flow: item.element, uuid, pageId });
      }
      return;
    }
    else if (item.type === REUSABLE_SECTION) {
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_BLOCK]: {
          blockId: item.id,
          blocks: item.element.elementDetails,
          variableContract: item.element?.variableContract || [],
          style: item.element.style,
          uuid,
          pageId,
          dropType: 'over',
          dropItem: 'last',
          currentPageId: item.pageId,
        },
      });

      if (checkSessionUserAccess(userDetails, 'master')) {
        updateArtboard({
          [CONTEXT_KEYS.REUSABLE_TYPE]: REUSABLE_SECTION,     
        });
      }else{
        updateArtboard({
          [CONTEXT_KEYS.CONFIMATION_PAGE_MODAL]: true,
          [CONTEXT_KEYS.REUSABLE_TYPE]: REUSABLE_SECTION,         
        });
      }
      
    }  else if (item.type === COMPLEX_EMAIL) {
      replaceEmailTemplate(item, methods, businessLogics, setBusinessLogics);
    } else {
      const newBlockUUID = methods.setPage('addBlock', {
        uuid,
        block: Object.assign({}, item.element.elementDetails),
        pageId,
        dropType: 'over',
        dropItem: 'last',
        currentPageId: item.pageId,
      });
      // TODO : Activate current block for editing
      if (newBlockUUID) {
        const elementBlocks = [DYNAMIC_TABLE_BLOCK, BUTTON_BLOCK, LINK_BLOCK];
        updateArtboard({
          [CONTEXT_KEYS.CURRENT_BLOCK]: newBlockUUID,
          [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: item.element.elementDetails.type,
          [CONTEXT_KEYS.CURRENT_PAGE]: uuid,
          [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]:
            item.element.elementDetails.type === CREATIVE_BLOCK,
          [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]:
            elementBlocks.includes(item.element.elementDetails.type),
        });
      }
    }
  }, [businessLogics]);

  const [isOver, drop] = useDrop({
    canDrop: () => editable,
    accept: isDocumentOrFlowDesign ? [...dropAcceptKeys, REUSABLE_SECTION] : dropAcceptKeys,
    collect: monitor => !!monitor.isOver({ shallow: true }),
    drop: onDrop
  });

  useEffect(() => {
    if (editable) {
      // Changed to parent component to allow ease of drop when the page is not yet filled with block
      drop(scrollRef);
    }
  }, [editable]);

  const [, drag, preview] = useDrag({
    item: { id: uuid, name: '', type:'replaceFlow', element:pageId },
    canDrag: isDndFlow,
    begin: () => {
      const flowId = methods.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][uuid];
      const template = getLatestArtboard()[CONTEXT_KEYS.SELECTED_TEMPLATE_PAGES_IN_SIDEBAR_DND];
      const generatedObj = template?.flows?.find(flow => flow.flowId === flowId);
      console.log("generatedObj :: ", generatedObj, flowId)     
      return ({
        type: 'replaceFlow',
        id: uuid,
        element: generatedObj,
        boundaries: pageRef.current?.getBoundingClientRect(),
        template
      });
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  isDndFlow && drag(pageRef);

  const blockElementStyles = useCallback((e, blockId) => {
    if (getElementStyles) {
      getElementStyles(e, blockId, false);
    }
  }, [getElementStyles]);

  const currentEditor = editingBlockOrElement?.current?.getEditor()?.root;

  const pageWrapperRef = useRef(undefined);
  const ScrollingComponent = useMemo(() => withScrolling(PagesWrapper, pageWrapperRef), []);


  const onDoubleClick = useCallback(() => {
    updateArtboard({ [CONTEXT_KEYS.FLOW_EDIT_MODE]: uuid });
  }, []);

  const previewModalSelector = useCallback(store => store[CONTEXT_KEYS.PREVIEW_MODAL], []);
  const [isPreviewModal] = useArtboardContext(previewModalSelector, `DesignPage-${uuid}`);

  return (
    <>
      <div
        id={`dim-bg-${pageId}`}
        style={{ position: 'fixed',  display: blockEditing ? 'block' : 'none', backgroundColor: 'rgba(0, 0, 0, 0.1)', top:0, left: 0, height: '100%', width: '100%' }}
        onClick={disableEditing}
      />
      <ContextMenuContainer pageId={uuid} />
      {blockEditing && currentEditor && (
        <VariableMenuContainer
          pageRef={pageRef}
          currentEditor={currentEditor}
          blockEditing={blockEditing}
          currentBlock={currentBlock}
        />
      )}
      <PageContent
        className={`page-content ${pageOverflowEnabled ? 'no-space-on-top' : ''}`}
        id={uuid}
        data-pageid={uuid}
        data-pagenum={flowNumber}
        ref={pageRef}
        width={width}
        height={height}
        backgroundColor={"#F5F8FA"}
        isOver={isOver}
        scaling={scaling}
        rulerEnabled={true}
        previewModal={isPreviewModal}
        isEmailTemplate={true}
        onDoubleClick={isStandalone ? onDoubleClick : dummyCallback}
        editable={editable}
      >
        <ScrollingComponent id={COMPLEX_DOCUMENT_PAGE_WRAPPER} style={{ overflow: 'visible', backgroundColor }}>
          <BlockLayout
            uuid={uuid}
            pageId={pageId}
            pageIndex={flowNumber}
            editable={editable}
            useStore={useStore}
            customClassName={'vertical-layout'}
            scaling={scaling}
            getElementStyles={blockElementStyles}
            isPageVisible={true}
            isEmailTemplate={true}
            isMobileView={isMobileView}
            isStandaloneFlow={isStandalone}
          />
        </ScrollingComponent>
        <div
          id={`dim-bg-${pageId}`}
          style={{ position: 'fixed', zIndex: 1, backgroundColor: 'rgba(0, 0, 0, 0.1)', top:0, left: 0, height: '100%', width: '100%', display: blockEditing ? 'block' : 'none' }}
          onClick={disableEditing}
        />
      </PageContent>
      <ToolTipPopup
        id={'variableTooltipPopUp'}
        style={{ position: 'fixed',
          display:'flex',
          zIndex:'40',
          alignItems:'center',
          justifyContent:'center',
          background:'black',
          color:'white',
          borderRadius:'4px',
          minWidth:'fit-content',
          padding:'4px 8px',
          top: 0,  
          boxShadow: 'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px'}}>

      </ToolTipPopup>
    </>
  )
}

const DesignPageWithTranslation = React.memo(withTranslation(NAMESPACES)(DesignPage));

const ComplexEmailPage = ({ t, uuid, flowNumber, isVisible, visibilityRef, scaling, EmailTemplateTab}) => {

  const customSelector = useCallback((store) => store[uuid], []);
  const [pageDetails] = useDocumentContext(customSelector, `ComplexPage-${uuid}`);

  const scrollRef = useRef(null);

  const [childHeight, setHeight] = useState(pageDetails?.height);
  const [hideBlockSettings, setHideBlockSettings] = useState(false);

  const customArtboardSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_PAGE] === uuid && store[CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE] !== true, []);
  const [blockEditing, updateArtboard] = useArtboardContext(customArtboardSelector, `ComplexPage-${uuid}`);

  const currentPageEditing = useRef(blockEditing);
  const [elementStylesCallback, setElementStylesCallback] = useState(null);

  const pageOverflowSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [pageOverflowEnabled] = useArtboardContext(pageOverflowSelector, `ComplexPage-${uuid}`);

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector, `ComplexPage-${uuid}`);

  const rulerSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_RULER], []);
  const [rulerEnabled] = useArtboardContext(rulerSelector, `ComplexPage-${uuid}`);

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [CurrentDesign] = useArtboardContext(getCurrentDesign);

  const standaloneSelector = useCallback(store => (store[uuid].isStandalone && CurrentDesign === 'document'), [CurrentDesign]);
  const [isStandalone] = useDocumentContext(standaloneSelector, `ComplexPage-${uuid}`);

  const [actualScaling, setActualScaling] = useState(scaling === 'fit' ? Number.parseFloat((visibilityRef.getBoundingClientRect().height - 200) / pageDetails.height).toFixed(2) : scaling);

  const rulerAdjusterSelector = useCallback(store => store[CONTEXT_KEYS.RULER_WIDTH_ADJUSTER], []);
  const [isRulerWidthAdjuster] = useArtboardContext(rulerAdjusterSelector, `DesignPage-${uuid}`);

  const dndFlowSelector = useCallback(store => store[CONTEXT_KEYS.DND_FLOW], []);
  const [isDndFlow] = useArtboardContext(dndFlowSelector, `DesignPage-${uuid}`);

  const mobileViewSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.IS_MOBILE_VIEW], []);
  const [isMobileView] = useEmailContext(mobileViewSelector, `DesignPage-${uuid}`);

  const emailSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE], []);
  const [isEmailTemplate] = useDocumentContext(emailSelector, `ComplexPage-${uuid}`);

  const { Provider, useStore } = useMemo(createResizeObserver, [pageOverflowEnabled]);

  useEffect(() => {
    setActualScaling(scaling === 'fit' ? Number.parseFloat((visibilityRef.getBoundingClientRect().height - 200) / pageDetails.height).toFixed(2) : scaling)
  }, [scaling])

  useEffect(() => {
    currentPageEditing.current = blockEditing; // Hoping that setting this first preceeding the next use effect would help
  }, [blockEditing]);

  useEffect(() => {
    if (!isVisible && currentPageEditing.current) { // TODO : Should the scroll be disabled on opening page settings
      // Use effect was getting called twice with combinations of values, erasing the newly set current page
      updateArtboard({
        [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false,
        [CONTEXT_KEYS.CURRENT_BLOCK]: '',
        [CONTEXT_KEYS.CURRENT_PAGE]: '',
        [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false,
      });
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      updateArtboard({ [CONTEXT_KEYS.CURRENT_PAGE]: '', [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false });
    }
  }, []);

  const showHideBlockSettings = useCallback((e => {
    let scrollElm = document.getElementById("complex-page-wrapper");
    let isForceHide =  e.target.nodeName.toLowerCase() === 'canvas' || (scrollElm?.scrollTop === 0 && (e?.clientY < (isEmailTemplate ? 0 : 155)));
    if (isForceHide) {
      setHideBlockSettings(true);
    } else {
      let targetElm = e.target;
      if (targetElm) {
        if (
          targetElm.id?.includes('complex-page') ||
          targetElm.getAttribute('class')?.includes('droppable empty') ||
          targetElm.getAttribute('class')?.includes('vertical-layout')
        ) {
          setHideBlockSettings(true);
        } else {
          setHideBlockSettings(false);
        }
      } else {
        setHideBlockSettings(false);
      }
    }
  }), []);

  const onMouseLeave = useCallback(() => {     
    updateArtboard({ [CONTEXT_KEYS.FLOW_EDIT_MODE]: '' });
  }, []);

  const isEmailTemplateTab = {
    maxWidth: isMobileView ? `${actualScaling * 390 + 30}px` : pageDetails?.width,
    background: pageDetails?.backgroundColor
  }

  const designSystem = {
    maxWidth: isMobileView ? `${actualScaling * 390 + 30}px` : pageDetails?.width,
    height: isMobileView ? '500px' : 'auto',
    margin: '0 auto',
    background: isMobileView ? 'transparent' : pageDetails?.backgroundColor
  }
  
 return (
    <Provider>
      {pageDetails?.pages?.map(pageId => (
        <EditorWrapper
          id={`scroll-${pageId}`}
          key={`scroll-${pageId}`}   
          rulerEnabled={isRulerWidthAdjuster} 
          dndFlow= {isDndFlow}
          ref={scrollRef}
        >
          {!isMobileView && <EmailBackground pageDetails={pageDetails} />}
          <EditingContainer
            transparent={false} // TODO : Enable for versions preview
            rulerEnabled={true} // TODO : integrate with ruler
            style={
              EmailTemplateTab ? isEmailTemplateTab : designSystem
            }
            outline={!editable}
            id={`editingContainer-${pageId}`}
            hideBlockSettings={hideBlockSettings}
            onMouseMove={(e) => editable && !blockEditing && showHideBlockSettings(e)}
            onMouseLeave={isStandalone ? onMouseLeave : dummyCallback}
            className={isMobileView ? 'scrollable' : undefined}
          >
            {editable &&  !isStandalone && <BlockLayoutSettings
              pageId={pageId}
              parentUUID={uuid}
              customRef={setElementStylesCallback}
              isEmailTemplate={true}
              isMobileView={isMobileView}
            />}
            <DesignPageWithTranslation
              key={pageId}
              uuid={uuid}
              pageId={pageId}
              scaling={actualScaling}
              rulerEnabled={rulerEnabled}
              pageOverflowEnabled={pageOverflowEnabled}
              flowNumber={flowNumber}
              widthInMM={pageDetails?.widthInMM}
              heightInMM={pageDetails?.heightInMM}
              width={isMobileView ? 390 : pageDetails?.width}
              height={500}
              backgroundColor={pageDetails?.color}
              setParentHeight={setHeight}
              getElementStyles={elementStylesCallback}
              editable={editable && !isStandalone}
              CurrentDesign={CurrentDesign}
              isStandalone={isStandalone}
              isMobileView={isMobileView}
              useStore={useStore}
              scrollRef={scrollRef}
            />
          </EditingContainer>
        </EditorWrapper>
      ))}
      {  isVisible && <CustomDragLayer scaling={actualScaling} />}
    </Provider>
  );
}

export default React.memo(withTranslation(['COMPLEX_PAGE_OPTIONS','COMPLEX_EMAIL_LAYOUT_SETTINGS'])(ComplexEmailPage));